/**
 * Phase 1.5.3 — 顧客 LIFF 予約詳細画面。
 *
 * Worker `GET /api/my-reservations/:id` から取得した予約を表示する。
 * `can_cancel === true` のときのみキャンセルボタンを表示。
 * キャンセルボタンを押すと cancelConfirm モーダルを開く。
 */

import { setRoot, showError } from "../render";
import { fetchMyReservationDetail } from "../api";
import type { MyReservation } from "../types";

const STATUS_LABELS: Record<string, string> = {
  confirmed: "確定",
  completed: "面談済",
  no_show: "面談済", // 顧客視点では completed と同等扱い
  canceled: "キャンセル",
};

const CONTACT_LABELS: Record<string, string> = {
  zoom: "オンライン面談",
  line_call: "LINE通話",
};

const BUSINESS_LABELS: Record<string, string> = {
  整備工場: "整備工場",
  中古車販売店: "中古車販売店",
  兼業: "兼業",
  その他: "その他",
};

/**
 * 予約詳細を取得して表示する。
 */
export async function renderMyReservationDetail(id: string): Promise<void> {
  setRoot(`
    ${headerHtml()}
    <div class="text-center text-gray-500 mt-8">読み込み中...</div>
  `);

  let reservation: MyReservation;
  try {
    reservation = await fetchMyReservationDetail(id);
  } catch (e: any) {
    showError(`予約の取得に失敗しました：${e?.message ?? e}`);
    setRoot(`
      ${headerHtml()}
      <div class="text-center text-red-600 mt-8 px-2">
        予約情報を取得できませんでした。
      </div>
      <div class="mt-6 text-center">
        <button id="btn-back-to-list"
          class="px-6 py-3 bg-gray-200 rounded-lg">
          一覧に戻る
        </button>
      </div>
    `);
    bindBackToList();
    return;
  }

  paint(reservation);
}

function paint(r: MyReservation): void {
  setRoot(`
    ${headerHtml()}
    ${reservationCardHtml(r)}
    ${actionsHtml(r)}
  `);
  bindBackToList();
  bindCancelButton(r);
}

function headerHtml(): string {
  return `
    <header class="mb-3 pb-2 border-b flex items-center justify-between">
      <button id="btn-back-to-list"
        class="text-sm text-gray-500 hover:text-gray-700 px-2 py-1">
        ‹ 一覧へ
      </button>
      <h1 class="text-lg font-bold text-gray-900">予約詳細</h1>
      <span class="w-16"></span>
    </header>
  `;
}

function reservationCardHtml(r: MyReservation): string {
  const dateLabel = formatDateWithWeekday(r.reservation_date);
  // confirmed かつキャンセル可能期間内なら「変更可」、期限超過 confirmed は「確定」のまま。
  // 一覧画面（myReservations.ts）と同じ判定を踏襲。
  const isChangeable = r.status === "confirmed" && r.can_cancel;
  const statusLabel = isChangeable ? "変更可" : (STATUS_LABELS[r.status] ?? r.status);
  const badgeColor = isChangeable
    ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
    : badgeClass(r.status);
  const contactLabel = CONTACT_LABELS[r.contact_method] ?? r.contact_method;
  const businessLabel = r.business_type
    ? BUSINESS_LABELS[r.business_type] ?? r.business_type
    : "—";

  const meetUrlSection =
    r.meet_url && r.contact_method === "zoom"
      ? `
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">URL</dt>
          <dd class="break-all">
            <a href="${escapeHtml(r.meet_url)}" target="_blank" rel="noopener noreferrer"
              class="text-blue-600 underline">
              ${escapeHtml(r.meet_url)}
            </a>
          </dd>
        </div>
      `
      : "";

  const cancelInfoSection =
    r.status === "canceled" && r.cancel_reason
      ? `
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">状態</dt>
          <dd class="text-gray-700">キャンセル済み</dd>
        </div>
      `
      : "";

  return `
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="flex items-start justify-between gap-3 mb-3">
        <div>
          <div class="text-xs text-gray-500">日時</div>
          <div class="text-lg font-bold text-gray-900">
            ${escapeHtml(dateLabel)} ${escapeHtml(r.reservation_time)}
          </div>
        </div>
        <span class="text-xs px-2 py-0.5 rounded ${badgeColor} flex-shrink-0 mt-1">
          ${escapeHtml(statusLabel)}
        </span>
      </div>

      <dl class="text-sm text-gray-800 space-y-2">
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">所要時間</dt>
          <dd>${r.duration_minutes}分</dd>
        </div>
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">連絡方法</dt>
          <dd>${escapeHtml(contactLabel)}</dd>
        </div>
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">業種</dt>
          <dd>${escapeHtml(businessLabel)}</dd>
        </div>
        ${meetUrlSection}
        ${cancelInfoSection}
      </dl>
    </div>
  `;
}

function actionsHtml(r: MyReservation): string {
  if (r.status === "canceled") {
    return `
      <div class="mt-6 text-center text-sm text-gray-500">
        この予約はキャンセル済みです。
      </div>
      <div class="mt-3 text-center">
        <button id="btn-go-calendar"
          class="px-6 py-3 bg-brand text-white rounded-lg">
          カレンダーから新しく予約する
        </button>
      </div>
    `;
  }

  if (r.status === "completed" || r.status === "no_show") {
    return `
      <div class="mt-6 text-center text-sm text-gray-500">
        この予約は完了しています。
      </div>
    `;
  }

  // status === "confirmed"
  if (!r.can_cancel) {
    return `
      <div class="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
        予約時間の 2 時間前を過ぎたため、この画面からはキャンセルできません。<br>
        キャンセルご希望の場合は、このトークでお知らせください😊
      </div>
    `;
  }

  return `
    <div class="mt-6 flex flex-col gap-2 items-center">
      <button id="btn-reschedule"
        class="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg w-full max-w-xs font-semibold">
        📅 日時を変更する
      </button>
      <button id="btn-cancel"
        class="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg w-full max-w-xs">
        この予約をキャンセルする
      </button>
    </div>
    <div class="mt-2 text-center text-xs text-gray-500">
      ${formatDeadline(r.cancel_deadline_iso)} まで変更・キャンセルできます
    </div>
  `;
}

function badgeClass(status: string): string {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800";
    case "completed":
    case "no_show":
      return "bg-blue-100 text-blue-800";
    case "canceled":
      return "bg-gray-200 text-gray-600";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function bindBackToList(): void {
  document.getElementById("btn-back-to-list")?.addEventListener("click", async () => {
    const { renderMyReservations } = await import("./myReservations");
    await renderMyReservations();
  });
  document.getElementById("btn-go-calendar")?.addEventListener("click", async () => {
    const { renderStep1 } = await import("../steps/step1Date");
    await renderStep1();
  });
}

function bindCancelButton(r: MyReservation): void {
  document.getElementById("btn-cancel")?.addEventListener("click", async () => {
    const { openCancelConfirm } = await import("./cancelConfirm");
    openCancelConfirm(r);
  });

  // Phase 1.5.4 拡張：日時変更ボタン
  document.getElementById("btn-reschedule")?.addEventListener("click", async () => {
    const { startReschedule } = await import("../store");
    startReschedule(r);
    const { renderStep1 } = await import("../steps/step1Date");
    await renderStep1();
  });
}

// ===== ヘルパー =====

function formatDateWithWeekday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${m}/${d}(${w})`;
}

function formatDeadline(iso: string): string {
  // "2026-05-10T12:00:00+09:00" → "5月10日(日) 12:00"
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, , mo, d, hh, mi] = m;
  const dt = new Date(Number(m[1]), Number(mo) - 1, Number(d));
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${Number(mo)}月${Number(d)}日(${w}) ${hh}:${mi}`;
}

function escapeHtml(s: unknown): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
